/*
  Transient added-to-cart confirmation on the product-card cart circle (VT-19d, 2026-08-29).

  BUILD-SPEC ruling 10 shipped `.product-card__cart--added` in component-product-card.css with
  nothing toggling it (PR-0039, "Deferred"). This is the wiring and nothing else: the add request,
  the cart drawer and product-form.js are untouched — the circle just confirms for 2.5s and reverts.

  Why one delegated listener instead of a custom element per card: a PLP renders 16+ cards and the
  home page several carousels, so a single document-level submit listener plus one pubsub
  subscription is strictly cheaper than N upgraded elements. It also survives the innerHTML swap
  product-recommendations performs on the PDP (global.js), which would otherwise tear down and
  re-run every per-card connectedCallback.

  Identifying the initiator. product-form.js publishes `{ source, productVariantId, cartData }` on
  success, and the PDP buy box publishes the identical `source: 'product-form'` — so the payload
  alone cannot say which element started the add, and a variant id can appear on several cards at
  once. The initiator is captured at submit time instead: a capture-phase `submit` listener runs
  before product-form.js's own bubble-phase handler, and a `.product-card__cart` button inside the
  submitted form is what marks the add as card-initiated (the PDP buy box and the quick-add modal
  both submit a `.product-form__submit` button). That button is parked under its variant id and
  claimed by the first matching `cartUpdate`, so exactly one circle ticks.

  Failure paths. On an add error product-form.js publishes `cartError`, never `cartUpdate`, so the
  parked button is dropped there and nothing ticks. A fetch that rejects outright publishes nothing
  at all, so parked buttons also expire: a later unrelated add of the same variant can never claim
  a stale one.
*/
if (!window.productCardAddedState) {
  const CART_SELECTOR = '.product-card__cart';
  const ADDED_CLASS = 'product-card__cart--added';
  const ADDED_DURATION = 2500;
  const PENDING_TTL = 15000;

  class ProductCardAddedState {
    #controller = new AbortController();
    #unsubscribers = [];
    #pending = new Map();
    #timers = new Map();

    constructor() {
      document.addEventListener('submit', this.#onSubmit, {
        capture: true,
        signal: this.#controller.signal,
      });

      this.#unsubscribers = [
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.#onCartUpdate),
        subscribe(PUB_SUB_EVENTS.cartError, this.#onCartError),
      ];
    }

    /**
     * Disposal path. Nothing in the theme calls it — the listener is document-level and is meant to
     * outlive every section re-render — but the teardown has to exist and be correct for the module
     * to be safe to unload from a test page or a future owner-facing toggle.
     */
    destroy() {
      this.#controller.abort();
      for (const unsubscribe of this.#unsubscribers) unsubscribe();
      this.#unsubscribers = [];
      for (const timer of this.#timers.values()) clearTimeout(timer);
      this.#timers.clear();
      this.#pending.clear();
    }

    #onSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const button = form.querySelector(CART_SELECTOR);
      const variantId = form.querySelector('[name="id"]')?.value;
      if (!button || !variantId) return;

      this.#pending.set(String(variantId), { button, at: Date.now() });
    };

    #onCartUpdate = (event) => {
      if (event?.source !== 'product-form') return;

      const key = String(event.productVariantId ?? '');
      const entry = this.#pending.get(key);
      this.#pending.delete(key);
      if (!entry || Date.now() - entry.at > PENDING_TTL) return;

      this.#tick(entry.button);
    };

    #onCartError = (event) => {
      if (event?.source !== 'product-form') return;
      this.#pending.delete(String(event.productVariantId ?? ''));
    };

    /** A second add inside the window restarts the 2.5s rather than stacking a second revert. */
    #tick(button) {
      clearTimeout(this.#timers.get(button));
      this.#setAdded(button, true);
      this.#timers.set(
        button,
        setTimeout(() => {
          this.#timers.delete(button);
          this.#setAdded(button, false);
        }, ADDED_DURATION)
      );
    }

    #setAdded(button, added) {
      button.classList.toggle(ADDED_CLASS, added);

      // The button already carries aria-live="polite" and its accessible name is computed from its
      // own text plus the card title, so swapping the visually-hidden face — Dawn's own sold-out
      // pattern, reused — announces the state change without a second live region.
      const soldOutVisible =
        button.querySelector('.sold-out-message')?.classList.contains('hidden') === false;
      button.querySelector('.product-card__cart-added-message')?.classList.toggle('hidden', !added);
      button
        .querySelector('.product-card__cart-label')
        ?.classList.toggle('hidden', added || soldOutVisible);
    }
  }

  window.productCardAddedState = new ProductCardAddedState();
}
